import {
  useEffect, useMemo, useRef,
} from 'react';
import { useGetAgentSessionBomByIdQuery } from 'store/slices/valueChainMap/apis/valueChainMapApi';
import { getUnitIdFromNodeId } from 'utils/AICreatorHelper';

import type IUseAICreatorBoMBomItemParams from '../interfaces/IUseAICreatorBoMBomItemParams';

const useAICreatorBoMBomItem = ({
  session,
  valueChainMapId,
  activeTabNodeId,
  tabs,
  agentSessionBom,
}: IUseAICreatorBoMBomItemParams) => {
  const currentTabNodeId = activeTabNodeId || tabs[0]?.nodeId || '';

  const currentUnitId = useMemo(
    () => getUnitIdFromNodeId(currentTabNodeId),
    [currentTabNodeId],
  );

  const resolvedBomId = useMemo(() => agentSessionBom?.data?.find(
    (bom) => currentUnitId !== null && bom.sessionUnit.unitId === currentUnitId,
  )?.id
  || 0, [agentSessionBom?.data, currentUnitId]);

  const stableBomRef = useRef<{ unitId: number | null, bomId: number }>({
    unitId: null,
    bomId: 0,
  });

  useEffect(() => {
    const prevState = stableBomRef.current;

    if (prevState.unitId !== currentUnitId) {
      stableBomRef.current = {
        unitId: currentUnitId,
        bomId: resolvedBomId,
      };
      return;
    }

    if (resolvedBomId) {
      stableBomRef.current = {
        unitId: currentUnitId,
        bomId: resolvedBomId,
      };
    }
  }, [currentUnitId, resolvedBomId]);

  const currentBomId = currentUnitId === stableBomRef.current.unitId
    ? stableBomRef.current.bomId
    : resolvedBomId;

  const { data: agentSessionBomItem, isFetching: isAgentSessionBomItemFetching } = useGetAgentSessionBomByIdQuery({
    valueChainMapId: valueChainMapId || '',
    sessionId: session?.id || 0,
    bomId: currentBomId || 0,
  }, {
    skip: !session?.id || !valueChainMapId || !currentBomId,
  });

  return {
    currentTabNodeId,
    currentUnitId,
    currentBomId,
    agentSessionBomItem,
    isAgentSessionBomItemFetching,
  };
};

export default useAICreatorBoMBomItem;
